import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser as getMe } from '../api/auth';
import { getUserMemorials } from '../api/memorials';
import { logout, getEmail } from '../auth';
import './ProfilePage.css';

const ProfilePage = () => {
    const navigate = useNavigate();

    // State
    const [user, setUser] = useState(null);
    const [qrList, setQrList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchUserDataAndMemorials();
    }, []);

    const fetchUserDataAndMemorials = async () => {
        try {
            setLoading(true);
            setError(null);

            let email = getEmail();
            if (!email) {
                const me = await getMe();
                email = me?.email;
            }
            if (!email) throw new Error('E-Mail-Adresse konnte nicht ermittelt werden');

            setUser({
                username: email.split('@')[0] || 'Benutzer',
                avatar: null,
                email
            });

            const memorials = await getUserMemorials();

            const mapped = (Array.isArray(memorials) ? memorials : []).map((m) => ({
                id: m.id,
                qrId: m.code ? `QR-${m.code}` : `MEM-${m.id}`,
                firstName: m.firstName,
                lastName: m.lastName,
                plan: m.planType ? String(m.planType) : 'BASIC',
                templateType: (m.templateType || '').toLowerCase() || 'gold'
            }));

            setQrList(mapped);
        } catch (err) {
            console.error('Error fetching profile:', err);

            const backendMsg =
                (typeof err?.response?.data === 'string' && err.response.data) ||
                err?.response?.data?.message;

            setError(backendMsg || err.message || 'Profildaten konnten nicht geladen werden');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (memorialId) => {
        navigate(`/edit-memorial/${memorialId}`);
    };

    const handleLogout = () => logout();

    // Loading
    if (loading) {
        return (
            <div className="profile-page">
                <div className="profile-particles">
                    <div className="particles-glow" />
                    {[...Array(20)].map((_, index) => (
                        <div key={index} className="particle" />
                    ))}
                </div>
                <div className="loading-container">
                    <div className="spinner" />
                    <p>Ihr Profil wird geladen…</p>
                </div>
            </div>
        );
    }

    // Error
    if (error) {
        return (
            <div className="profile-page">
                <div className="profile-particles">
                    <div className="particles-glow" />
                    {[...Array(20)].map((_, index) => (
                        <div key={index} className="particle" />
                    ))}
                </div>

                <div className="error-container">
                    <h2>Fehler</h2>
                    <p>{error}</p>

                    <div className="error-actions">
                        <button
                            className="retry-btn"
                            onClick={() => fetchUserDataAndMemorials()}
                        >
                            Erneut versuchen
                        </button>

                        <button
                            className="login-redirect-btn"
                            onClick={() => navigate('/login')}
                        >
                            Zur Anmeldung
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // UI
    return (
        <div className="profile-page">
            <div className="profile-particles">
                <div className="particles-glow" />
                {[...Array(20)].map((_, index) => (
                    <div key={index} className="particle" />
                ))}
            </div>

            <div className="profile-container">
                <motion.div
                    className="profile-header"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <div className="user-info">
                        <motion.div
                            className="user-avatar"
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            {user?.avatar ? (
                                <img src={user.avatar} alt="Benutzer-Avatar" />
                            ) : (
                                <div className="avatar-placeholder">
                                    <svg viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                    </svg>
                                </div>
                            )}
                        </motion.div>

                        <motion.h2
                            className="username"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                        >
                            {user?.username || 'Benutzer'}
                        </motion.h2>
                    </div>

                    <div className="header-actions">
                        <motion.button
                            className="home-btn"
                            onClick={() => navigate('/')}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            STARTSEITE
                        </motion.button>

                        <motion.button
                            className="logout-btn"
                            onClick={handleLogout}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            ABMELDEN
                        </motion.button>
                    </div>
                </motion.div>

                <motion.div
                    className="qr-list-section"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                >
                    <h3 className="list-title">Meine Liste</h3>

                    {qrList.length === 0 ? (
                        <div className="empty-state">
                            <p className="empty-message">
                                Sie haben noch keine Gedenkseiten
                            </p>
                            <motion.button
                                className="add-qr-btn"
                                onClick={() => navigate('/order')}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                + Fügen Sie Ihren ersten QR-Code hinzu
                            </motion.button>
                        </div>
                    ) : (
                        <>
                            <div className="qr-list">
                                {qrList.map((qr, index) => (
                                    <motion.div
                                        key={qr.id}
                                        className={`qr-item-card ${qr.templateType}`}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.5, delay: 0.7 + index * 0.1 }}
                                        whileHover={{ scale: 1.01 }}
                                    >
                                        <div className="qr-info">
                                            <div className="qr-id">{qr.qrId}</div>
                                            <div className="qr-name">{qr.firstName} {qr.lastName}</div>
                                            <div className="qr-plan">{qr.plan}</div>
                                        </div>

                                        <div className="qr-actions">
                                            <motion.button
                                                className="edit-btn"
                                                onClick={() => handleEdit(qr.id)}
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                            >
                                                Bearbeiten
                                            </motion.button>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>

                            <motion.button
                                className="add-qr-btn"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.6, delay: 1 }}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => navigate('/order')}
                            >
                                + Neuen QR-Code hinzufügen
                            </motion.button>
                        </>
                    )}
                </motion.div>
            </div>
        </div>
    );
};

export default ProfilePage;
